import type { SeriesOption } from 'echarts'

import { buildMarkArea } from '../buildMarkArea'
import { buildMarkLine } from '../buildMarkLine'
import {
  buildMarkPoint,
  type RetirementDots,
  type WealthDot,
} from '../buildMarkPoint'
import type { ChartTheme } from '../chartTheme'
import type { LabelledMilestone } from '../milestoneLabelPlacement'

/*
 * No highlight, and so no blur of everything else: a tap opens a year's detail,
 * and ECharts reads that same tap as a hover — which faded every other series,
 * the band marking the open year among them.
 */
const NO_EMPHASIS = { emphasis: { disabled: true } } as const

/**
 * How a run is drawn: the one being read, the one behind it, or not at all.
 *
 * A ghost keeps its colour and weight and gives up most of its ink — it is the
 * same series earlier, not a different one. Only the lead carries the
 * milestones, the band and the figures at retirement, which drawn twice would
 * read as four marks.
 */
export type RunVisibility = 'lead' | 'ghost' | 'hidden'

/** What the legend calls the line along the top of the plan's own stack. */
export const BASELINE_TOTAL_NAME = 'Current plan'

const RUN_OPACITY: Record<RunVisibility, number> = {
  lead: 1,
  ghost: 0.28,
  hidden: 0,
}

/** When a series lives in a multi-grid chart, pass the axis indices it belongs to. */
export type AxisOverride = { xAxisIndex: number; yAxisIndex: number }

export const buildNetWorthSeries = (
  theme: ChartTheme,
  milestones: LabelledMilestone[],
  visibility: RunVisibility,
  datasetIndex: number,
  suffix = '',
  axisOverride?: AxisOverride,
  selectedIndex?: number,
  /** What this line is worth at retirement, called out on it. */
  dot?: WealthDot
): SeriesOption => {
  const opacity = RUN_OPACITY[visibility]
  const isLead = visibility === 'lead'
  return {
    name: `Net Worth${suffix}`,
    type: 'line',
    ...axisOverride,
    datasetIndex,
    encode: { x: 'age', y: 'networth' },
    showSymbol: false,
    color: theme.dataset.networth.color,
    symbolSize: 7,
    symbol: 'circle',
    itemStyle: { opacity },
    lineStyle: { width: 2.5, opacity },
    areaStyle: {
      opacity,
      color: {
        type: 'linear',
        x: 0,
        y: 0,
        x2: 0,
        y2: 1,
        colorStops: [
          { offset: 0, color: theme.dataset.networth.color0 },
          { offset: 1, color: theme.dataset.networth.color1 },
        ],
      },
    },
    tooltip: { show: isLead },
    ...NO_EMPHASIS,
    markLine: isLead ? buildMarkLine(milestones, theme) : undefined,
    markArea: isLead ? buildMarkArea(theme, selectedIndex) : undefined,
    markPoint: isLead
      ? buildMarkPoint(theme, theme.dataset.networth.color, dot)
      : undefined,
  }
}

export const buildLiquidAssetsSeries = (
  theme: ChartTheme,
  visibility: RunVisibility,
  datasetIndex: number,
  suffix = '',
  axisOverride?: AxisOverride,
  dot?: WealthDot
): SeriesOption => {
  const opacity = RUN_OPACITY[visibility]
  const isLead = visibility === 'lead'
  return {
    name: `Liquid Assets${suffix}`,
    type: 'line',
    ...axisOverride,
    datasetIndex,
    encode: { x: 'age', y: 'liquidAssets' },
    showSymbol: false,
    color: theme.dataset.liquidAssets.color,
    symbolSize: 7,
    symbol: 'circle',
    itemStyle: { opacity },
    lineStyle: { width: 2.5, opacity },
    tooltip: { show: isLead },
    ...NO_EMPHASIS,
    markPoint: isLead
      ? buildMarkPoint(theme, theme.dataset.liquidAssets.color, dot)
      : undefined,
  }
}

const buildCashflowBar = (
  name: string,
  encodeY: string,
  color: string,
  visibility: RunVisibility,
  datasetIndex: number,
  stackId: string,
  axisOverride?: AxisOverride
): SeriesOption => ({
  name,
  type: 'bar',
  ...axisOverride,
  datasetIndex,
  stack: stackId,
  // Overlap the scenario and baseline stack groups so the column width
  // doesn't halve when the (invisible) other stack is also mounted.
  barGap: '-100%',
  encode: { x: 'age', y: encodeY },
  color,
  itemStyle: {
    opacity: RUN_OPACITY[visibility],
    borderRadius: [2, 2, 0, 0],
  },
  tooltip: { show: visibility === 'lead' },
  ...NO_EMPHASIS,
})

export const buildTaxesSeries = (
  theme: ChartTheme,
  visibility: RunVisibility,
  datasetIndex: number,
  stackId: string,
  suffix = '',
  axisOverride?: AxisOverride
): SeriesOption =>
  buildCashflowBar(
    `Taxes${suffix}`,
    'taxes',
    theme.dataset.taxes.color,
    visibility,
    datasetIndex,
    stackId,
    axisOverride
  )

export const buildExpensesSeries = (
  theme: ChartTheme,
  visibility: RunVisibility,
  datasetIndex: number,
  stackId: string,
  suffix = '',
  axisOverride?: AxisOverride
): SeriesOption =>
  buildCashflowBar(
    `Expenses${suffix}`,
    'expenses',
    theme.dataset.expenses.color,
    visibility,
    datasetIndex,
    stackId,
    axisOverride
  )

export const buildContributionsSeries = (
  theme: ChartTheme,
  visibility: RunVisibility,
  datasetIndex: number,
  stackId: string,
  suffix = '',
  axisOverride?: AxisOverride
): SeriesOption =>
  buildCashflowBar(
    `Contributions${suffix}`,
    'contributions',
    theme.dataset.contributions.color,
    visibility,
    datasetIndex,
    stackId,
    axisOverride
  )

export const buildWealthSeries = (
  theme: ChartTheme,
  milestones: LabelledMilestone[],
  visibility: RunVisibility,
  datasetIndex: number,
  suffix = '',
  axisOverride?: AxisOverride,
  selectedIndex?: number,
  /** What each line is worth at retirement, called out on it. */
  dots: RetirementDots = {}
): SeriesOption[] => {
  return [
    buildNetWorthSeries(
      theme,
      milestones,
      visibility,
      datasetIndex,
      suffix,
      axisOverride,
      selectedIndex,
      dots.networth
    ),
    buildLiquidAssetsSeries(
      theme,
      visibility,
      datasetIndex,
      suffix,
      axisOverride,
      dots.liquidAssets
    ),
  ]
}

export const buildCashflowSeries = (
  theme: ChartTheme,
  milestones: LabelledMilestone[],
  visibility: RunVisibility,
  datasetIndex: number,
  stackId: string,
  suffix = '',
  axisOverride?: AxisOverride,
  selectedIndex?: number
): SeriesOption[] => {
  return [
    buildIncomeSeries(
      theme,
      milestones,
      visibility,
      datasetIndex,
      suffix,
      axisOverride,
      selectedIndex
    ),
    buildTaxesSeries(
      theme,
      visibility,
      datasetIndex,
      stackId,
      suffix,
      axisOverride
    ),
    buildExpensesSeries(
      theme,
      visibility,
      datasetIndex,
      stackId,
      suffix,
      axisOverride
    ),
    buildContributionsSeries(
      theme,
      visibility,
      datasetIndex,
      stackId,
      suffix,
      axisOverride
    ),
  ]
}

export const buildIncomeSeries = (
  theme: ChartTheme,
  milestones: LabelledMilestone[],
  visibility: RunVisibility,
  datasetIndex: number,
  suffix = '',
  axisOverride?: AxisOverride,
  selectedIndex?: number
): SeriesOption => {
  const opacity = RUN_OPACITY[visibility]
  const isLead = visibility === 'lead'
  return {
    name: `Income${suffix}`,
    type: 'line',
    ...axisOverride,
    datasetIndex,
    encode: { x: 'age', y: 'income' },
    showSymbol: false,
    color: theme.dataset.income.color,
    symbolSize: 7,
    symbol: 'circle',
    itemStyle: { opacity },
    lineStyle: { width: 2.5, opacity },
    areaStyle: {
      opacity,
      color: {
        type: 'linear',
        x: 0,
        y: 0,
        x2: 0,
        y2: 1,
        colorStops: [
          { offset: 0, color: theme.dataset.income.color0 },
          { offset: 1, color: theme.dataset.income.color1 },
        ],
      },
    },
    tooltip: { show: isLead },
    ...NO_EMPHASIS,
    markLine: isLead ? buildMarkLine(milestones, theme) : undefined,
    markArea: isLead ? buildMarkArea(theme, selectedIndex) : undefined,
  }
}
